import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { usePagination } from '@tiles-erp/hooks';
import { ConfirmDialog, PageContainer, useSaveShortcut } from '@tiles-erp/ui';
import type { UserListItem } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { ApiError } from '../../lib/api-client';
import { useBranches } from '../products/branch-prices-api';
import { useRoles } from '../roles/api';
import { useOrgOptions } from '../organization/api';
import { useCreateUser, useDeleteUser, useUpdateUser, useUsers, type UserFilters } from './api';

const userFormSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z
    .string()
    .min(8, 'At least 8 characters')
    .regex(/[A-Z]/, 'Needs an uppercase letter')
    .regex(/[a-z]/, 'Needs a lowercase letter')
    .regex(/\d/, 'Needs a digit')
    .or(z.literal('')),
  firstName: z.string().trim().min(1, 'Required'),
  lastName: z.string().trim().min(1, 'Required'),
  isActive: z.boolean(),
  branchIds: z.array(z.string().uuid()),
  godownIds: z.array(z.string().uuid()),
  roleIds: z.array(z.string().uuid()).min(1, 'Assign at least one role'),
});

type UserFormValues = z.infer<typeof userFormSchema>;

const emptyValues: UserFormValues = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  isActive: true,
  roleIds: [],
  branchIds: [],
  godownIds: [],
};

export function UsersPage(): JSX.Element {
  const pagination = usePagination();
  const [filters, setFilters] = useState<UserFilters>({});
  const { data, isFetching } = useUsers(pagination.query, filters);
  const rolesQuery = useRoles({ page: 1, pageSize: 100, sortOrder: 'asc' });
  const branchesQuery = useBranches();
  const godownsQuery = useOrgOptions('/godowns');
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [deleting, setDeleting] = useState<UserListItem | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: emptyValues,
  });

  const openCreate = (): void => {
    setEditing(null);
    setServerError(null);
    form.reset(emptyValues);
    setDialogOpen(true);
  };

  const openEdit = (user: UserListItem): void => {
    setEditing(user);
    setServerError(null);
    form.reset({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      isActive: user.isActive,
      roleIds: user.roles.map((r) => r.id),
      branchIds: user.branches.map((b) => b.id),
      godownIds: user.godowns.map((g) => g.id),
    });
    setDialogOpen(true);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      if (editing) {
        await updateUser.mutateAsync({
          id: editing.id,
          firstName: values.firstName,
          lastName: values.lastName,
          isActive: values.isActive,
          roleIds: values.roleIds,
          branchIds: values.branchIds,
          godownIds: values.godownIds,
          ...(values.password ? { password: values.password } : {}),
          version: editing.version,
        });
      } else {
        if (!values.password) {
          form.setError('password', { message: 'Password is required for new users' });
          return;
        }
        await createUser.mutateAsync({
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          isActive: values.isActive,
          roleIds: values.roleIds,
          branchIds: values.branchIds,
          godownIds: values.godownIds,
          departmentIds: [],
        });
      }
      setDialogOpen(false);
    } catch (error) {
      setServerError(error instanceof ApiError ? error.message : 'Something went wrong');
    }
  });

  const columns = useMemo<ColDef<UserListItem>[]>(
    () => [
      { field: 'email', headerName: 'Email', minWidth: 220 },
      {
        headerName: 'Name',
        valueGetter: (p) => (p.data ? `${p.data.firstName} ${p.data.lastName}` : ''),
      },
      {
        field: 'roles',
        headerName: 'Roles',
        cellRenderer: (p: ICellRendererParams<UserListItem>) => (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%' }}>
            {p.data?.roles.map((r) => <Chip key={r.id} label={r.name} size="small" />)}
          </Stack>
        ),
      },
      {
        headerName: 'Branches',
        minWidth: 200,
        valueGetter: (p) => p.data?.branches.map((b) => b.name).join(', ') || 'Not assigned',
      },
      {
        field: 'isActive',
        headerName: 'Status',
        maxWidth: 120,
        cellRenderer: (p: ICellRendererParams<UserListItem>) => (
          <Chip
            label={p.data?.isActive ? 'Active' : 'Inactive'}
            color={p.data?.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        headerName: '',
        maxWidth: 110,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<UserListItem>) => (
          <>
            <IconButton size="small" aria-label="Edit" onClick={() => p.data && openEdit(p.data)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Delete"
              onClick={() => p.data && setDeleting(p.data)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ],
    [],
  );

  // Ctrl+S saves without reaching for the mouse.
  useSaveShortcut(() => void onSubmit(), dialogOpen);

  const selectedRoleIds = form.watch('roleIds');
  const selectedBranchIds = form.watch('branchIds');
  const isGodownStaff = (rolesQuery.data?.items ?? []).some(
    (role) => selectedRoleIds.includes(role.id) &&
      role.name === 'GODOWN STAFF',
  );
  const availableGodowns = (godownsQuery.data ?? []).filter(
    (godown) => godown.parentId && selectedBranchIds.includes(godown.parentId),
  );

  return (
    <PageContainer
      title="Users"
      subtitle="Manage user accounts, roles and branch access."
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New user
        </Button>
      }
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <Autocomplete
          options={rolesQuery.data?.items ?? []}
          value={(rolesQuery.data?.items ?? []).find((role) => role.id === filters.roleId) ?? null}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => {
            setFilters((current) => ({ ...current, roleId: value?.id }));
            pagination.setPage(1);
          }}
          sx={{ minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label="Role" placeholder="Search role" />}
        />
        <Autocomplete
          options={branchesQuery.data ?? []}
          value={(branchesQuery.data ?? []).find((branch) => branch.id === filters.branchId) ?? null}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => {
            setFilters((current) => ({ ...current, branchId: value?.id }));
            pagination.setPage(1);
          }}
          sx={{ minWidth: 240 }}
          renderInput={(params) => <TextField {...params} label="Branch" placeholder="Search branch" />}
        />
        <TextField
          select
          label="Status"
          value={filters.isActive === undefined ? '' : String(filters.isActive)}
          onChange={(event) => {
            const value = event.target.value;
            setFilters((current) => ({
              ...current,
              isActive: value === '' ? undefined : value === 'true',
            }));
            pagination.setPage(1);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="true">Active</MenuItem>
          <MenuItem value="false">Inactive</MenuItem>
        </TextField>
        <Button
          variant="text"
          disabled={!filters.roleId && !filters.branchId && filters.isActive === undefined}
          onClick={() => {
            setFilters({});
            pagination.setPage(1);
          }}
        >
          Clear filters
        </Button>
      </Stack>
      <DataTable
        rows={data?.items ?? []}
        columns={columns}
        meta={data?.meta}
        pagination={pagination}
        loading={isFetching}
        searchPlaceholder="Search by name or email…"
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.email}` : 'Create user'}</DialogTitle>
        <form onSubmit={onSubmit} noValidate autoComplete="off">
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {serverError && <Alert severity="error">{serverError}</Alert>}
              <TextField
                label="Email"
                type="email"
                disabled={Boolean(editing)}
                error={Boolean(form.formState.errors.email)}
                helperText={form.formState.errors.email?.message}
                {...form.register('email')}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="First name"
                  error={Boolean(form.formState.errors.firstName)}
                  helperText={form.formState.errors.firstName?.message}
                  {...form.register('firstName')}
                />
                <TextField
                  label="Last name"
                  error={Boolean(form.formState.errors.lastName)}
                  helperText={form.formState.errors.lastName?.message}
                  {...form.register('lastName')}
                />
              </Stack>
              <TextField
                label={editing ? 'New password (leave blank to keep)' : 'Password'}
                type="password"
                autoComplete="new-password"
                error={Boolean(form.formState.errors.password)}
                helperText={form.formState.errors.password?.message}
                {...form.register('password')}
              />
              <Controller
                control={form.control}
                name="roleIds"
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    label="Roles"
                    slotProps={{ select: { multiple: true } }}
                    value={field.value}
                    onChange={field.onChange}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  >
                    {(rolesQuery.data?.items ?? []).map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <Controller
                control={form.control}
                name="branchIds"
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    label="Branches"
                    slotProps={{ select: { multiple: true } }}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={branchesQuery.isPending || branchesQuery.isError}
                    error={Boolean(fieldState.error) || branchesQuery.isError}
                    helperText={fieldState.error?.message || (branchesQuery.isError
                      ? 'Could not load branches. Close and reopen this form to retry.'
                      : 'Select the branches this user can work in. No selection grants no branch access.')}
                  >
                    {[...(branchesQuery.data ?? []), ...(editing?.branches ?? []).filter(
                      (assigned) => !(branchesQuery.data ?? []).some((branch) => branch.id === assigned.id),
                    )].map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              {isGodownStaff && (
                <Controller
                  control={form.control}
                  name="godownIds"
                  render={({ field, fieldState }) => (
                    <TextField
                      select
                      label="Godowns *"
                      slotProps={{ select: { multiple: true } }}
                      value={field.value.filter((id) => availableGodowns.some((g) => g.id === id))}
                      onChange={field.onChange}
                      disabled={godownsQuery.isPending || selectedBranchIds.length === 0}
                      error={Boolean(fieldState.error) || godownsQuery.isError}
                      helperText={fieldState.error?.message || (selectedBranchIds.length === 0
                        ? 'Select a branch first.'
                        : 'Select the godowns whose delivery slips this user may print.')}
                    >
                      {availableGodowns.map((godown) => (
                        <MenuItem key={godown.id} value={godown.id}>
                          {godown.parentName} · {godown.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              )}
              <Controller
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label="Active"
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={form.formState.isSubmitting}>
              {editing ? 'Save changes' : 'Create user'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete user"
        message={`Delete ${deleting?.email}? The account is soft-deleted and can be restored by an engineer.`}
        destructive
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await deleteUser.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </PageContainer>
  );
}
